import React, { useState, useEffect, useCallback } from 'react';
import { getSoundManager, SFX_KEYS } from '../game/systems/SoundManager';

// ============ TYPES ============

interface WhirlpoolScanData {
  id: string;
  class: 'C1' | 'C2' | 'C3' | 'C4' | 'C5' | 'C6';
  className: string;
  status: 'stable' | 'unstable' | 'critical';
  effect: string;
  effectDescription: string;
  // Time info
  timeRemaining: number; // ms
  // Mass info
  massUsed: number;
  massLimit: number;
  // Content preview (revealed by scan)
  estimatedNPCs: { min: number; max: number };
  estimatedResources: { min: number; max: number };
  estimatedHazards: { min: number; max: number };
  hasBoss: boolean;
  // Rewards
  lootMultiplier: number;
  xpMultiplier: number;
  // Danger
  npcLevelRange: { min: number; max: number };
  dangerRating: number; // 1-10
}

interface WhirlpoolResearchPanelProps {
  isOpen: boolean;
  onClose: () => void;
  whirlpoolId: string | null;
  whirlpoolPosition: { x: number; y: number } | null;
  playerPosition: { x: number; y: number } | null;
  onEnterWhirlpool?: (whirlpoolId: string) => void;
}

// ============ CONSTANTS ============

const RESEARCH_DURATION_MS = 4000; // 4 seconds to scan

const CLASS_COLORS: Record<string, string> = {
  C1: '#4fc3f7',
  C2: '#29b6f6',
  C3: '#7e57c2',
  C4: '#ab47bc',
  C5: '#ef5350',
  C6: '#d32f2f',
};

const CLASS_DESCRIPTIONS: Record<string, string> = {
  C1: 'A minor disturbance. Safe for solo exploration.',
  C2: 'Moderate danger. Decent rewards for prepared sailors.',
  C3: 'Dangerous waters. Bring allies.',
  C4: 'Extreme peril. High-value targets within.',
  C5: 'Near-lethal conditions. Legendary rewards await.',
  C6: 'The deepest void. Only the strongest survive.',
};

const EFFECT_NAMES: Record<string, string> = {
  none: 'None',
  calm_belt: 'Calm Belt',
  raging_current: 'Raging Current',
  knock_up_stream: 'Knock-Up Stream',
  leviathan_waters: 'Leviathan Waters',
  storm_surge: 'Storm Surge',
  devils_triangle: "Devil's Triangle",
};

// Inject styles
const injectStyles = (): void => {
  const styleId = 'whirlpool-research-style';
  if (document.getElementById(styleId)) return;
  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    @keyframes whirlpoolScanProgress {
      0% { width: 0%; }
      100% { width: 100%; }
    }
    @keyframes whirlpoolScanPulse {
      0%, 100% { opacity: 0.6; }
      50% { opacity: 1; }
    }
    @keyframes whirlpoolScanLine {
      0% { transform: translateY(0); opacity: 0.8; }
      100% { transform: translateY(100%); opacity: 0; }
    }
  `;
  document.head.appendChild(style);
};

// ============ COMPONENT ============

export const WhirlpoolResearchPanel: React.FC<WhirlpoolResearchPanelProps> = ({
  isOpen,
  onClose,
  whirlpoolId,
  whirlpoolPosition,
  playerPosition,
  onEnterWhirlpool,
}) => {
  const [isScanning, setIsScanning] = useState<boolean>(false);
  const [scanProgress, setScanProgress] = useState<number>(0);
  const [scanData, setScanData] = useState<WhirlpoolScanData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    injectStyles();
  }, []);

  // Reset state when panel opens/closes
  useEffect(() => {
    if (isOpen && whirlpoolId) {
      setIsScanning(false);
      setScanProgress(0);
      setScanData(null);
      setError(null);
    }
  }, [isOpen, whirlpoolId]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Fetch scan data from server
  const fetchScanData = useCallback(async (): Promise<void> => {
    if (!whirlpoolId) return;

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const response = await fetch(`${apiUrl}/api/whirlpool/${whirlpoolId}/scan`);
      
      if (!response.ok) {
        throw new Error('Failed to scan whirlpool');
      }

      const data = await response.json();
      setScanData(data);
      
      const soundManager = getSoundManager();
      soundManager?.play(SFX_KEYS.RESEARCH_COMPLETE);
    } catch (err) {
      setError('Scan failed - whirlpool may have collapsed');
      console.error('Whirlpool scan error:', err);
    }
  }, [whirlpoolId]);

  // Start scanning
  const startScan = useCallback((): void => {
    if (isScanning || scanData) return;

    setIsScanning(true);
    setScanProgress(0);
    setError(null);

    const soundManager = getSoundManager();
    soundManager?.play(SFX_KEYS.RESEARCH_START);

    const startTime = Date.now();
    const progressInterval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(100, (elapsed / RESEARCH_DURATION_MS) * 100);
      setScanProgress(progress);

      if (progress >= 100) {
        clearInterval(progressInterval);
        setIsScanning(false);
        fetchScanData();
      }
    }, 50);
  }, [isScanning, scanData, fetchScanData]);

  // Calculate distance
  const distance = whirlpoolPosition && playerPosition
    ? Math.floor(Math.sqrt(
        Math.pow(whirlpoolPosition.x - playerPosition.x, 2) +
        Math.pow(whirlpoolPosition.y - playerPosition.y, 2)
      ))
    : 0;

  const canEnter = distance <= 50;

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        backgroundColor: '#0a0a0a',
        border: '2px solid #00ffff',
        padding: '0',
        fontFamily: 'monospace',
        color: '#ffffff',
        zIndex: 1000,
        minWidth: '400px',
        maxWidth: '450px',
      }}
    >
      {/* Header */}
      <div
        style={{
          backgroundColor: '#00ffff22',
          padding: '12px 16px',
          borderBottom: '1px solid #00ffff44',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <span style={{ color: '#00ffff', fontWeight: 'bold', fontSize: '14px' }}>
          WHIRLPOOL SCAN
        </span>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            color: '#888888',
            cursor: 'pointer',
            fontSize: '18px',
            padding: '0 4px',
          }}
        >
          X
        </button>
      </div>

      {/* Content */}
      <div style={{ padding: '16px' }}>
        {/* Not scanned yet */}
        {!scanData && !isScanning && !error && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ marginBottom: '16px', color: '#aaaaaa' }}>
              Scan this whirlpool to reveal its contents and danger level.
            </div>
            <button
              onClick={startScan}
              style={{
                backgroundColor: '#00ffff',
                color: '#000000',
                border: 'none',
                padding: '12px 32px',
                fontFamily: 'monospace',
                fontSize: '14px',
                cursor: 'pointer',
                fontWeight: 'bold',
              }}
            >
              BEGIN SCAN
            </button>
          </div>
        )}

        {/* Scanning in progress */}
        {isScanning && (
          <div>
            <div
              style={{
                marginBottom: '12px',
                color: '#00ffff',
                textAlign: 'center',
                animation: 'whirlpoolScanPulse 1s ease-in-out infinite',
              }}
            >
              SCANNING...
            </div>
            <div
              style={{
                height: '8px',
                backgroundColor: '#1a1a1a',
                border: '1px solid #333333',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  height: '100%',
                  backgroundColor: '#00ffff',
                  width: `${scanProgress}%`,
                  transition: 'width 50ms linear',
                }}
              />
            </div>
            <div
              style={{
                marginTop: '8px',
                textAlign: 'center',
                fontSize: '12px',
                color: '#666666',
              }}
            >
              {Math.floor(scanProgress)}%
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{ textAlign: 'center', color: '#ff4444' }}>
            {error}
          </div>
        )}

        {/* Scan Results */}
        {scanData && (
          <div>
            {/* Class Header */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '12px',
                paddingBottom: '12px',
                borderBottom: '1px solid #333333',
              }}
            >
              <span
                style={{
                  color: CLASS_COLORS[scanData.class],
                  fontWeight: 'bold',
                  fontSize: '18px',
                  marginRight: '12px',
                }}
              >
                [{scanData.class}]
              </span>
              <div>
                <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
                  {scanData.className}
                </div>
                <div style={{ fontSize: '11px', color: '#888888' }}>
                  {CLASS_DESCRIPTIONS[scanData.class]}
                </div>
              </div>
            </div>

            {/* Status & Time */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '8px',
                marginBottom: '12px',
              }}
            >
              <div>
                <div style={{ fontSize: '10px', color: '#666666' }}>STATUS</div>
                <div
                  style={{
                    color:
                      scanData.status === 'stable'
                        ? '#00ff88'
                        : scanData.status === 'unstable'
                        ? '#ffaa00'
                        : '#ff4444',
                    fontWeight: 'bold',
                  }}
                >
                  {scanData.status.toUpperCase()}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '10px', color: '#666666' }}>TIME LEFT</div>
                <div>
                  {Math.floor(scanData.timeRemaining / 60000)}m{' '}
                  {Math.floor((scanData.timeRemaining % 60000) / 1000)}s
                </div>
              </div>
              <div>
                <div style={{ fontSize: '10px', color: '#666666' }}>MASS USED</div>
                <div>
                  {Math.floor((scanData.massUsed / scanData.massLimit) * 100)}%
                </div>
              </div>
              <div>
                <div style={{ fontSize: '10px', color: '#666666' }}>DANGER</div>
                <div style={{ color: scanData.dangerRating >= 7 ? '#ff4444' : '#ffaa00' }}>
                  {scanData.dangerRating}/10
                </div>
              </div>
            </div>

            {/* Effect */}
            {scanData.effect !== 'none' && (
              <div
                style={{
                  marginBottom: '12px',
                  padding: '8px',
                  backgroundColor: '#1a1a1a',
                  border: '1px solid #333333',
                }}
              >
                <div style={{ fontSize: '10px', color: '#ffcc00', marginBottom: '4px' }}>
                  ENVIRONMENTAL EFFECT
                </div>
                <div style={{ fontWeight: 'bold' }}>
                  {EFFECT_NAMES[scanData.effect] || scanData.effect}
                </div>
                <div style={{ fontSize: '11px', color: '#888888', marginTop: '4px' }}>
                  {scanData.effectDescription}
                </div>
              </div>
            )}

            {/* Content Estimates */}
            <div
              style={{
                marginBottom: '12px',
                padding: '8px',
                backgroundColor: '#0a1a0a',
                border: '1px solid #00ff8844',
              }}
            >
              <div style={{ fontSize: '10px', color: '#00ff88', marginBottom: '8px' }}>
                ESTIMATED CONTENTS
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px', fontSize: '12px' }}>
                <div>
                  <span style={{ color: '#888888' }}>NPCs: </span>
                  {scanData.estimatedNPCs.min}-{scanData.estimatedNPCs.max}
                </div>
                <div>
                  <span style={{ color: '#888888' }}>Level: </span>
                  {scanData.npcLevelRange.min}-{scanData.npcLevelRange.max}
                </div>
                <div>
                  <span style={{ color: '#888888' }}>Resources: </span>
                  {scanData.estimatedResources.min}-{scanData.estimatedResources.max}
                </div>
                <div>
                  <span style={{ color: '#888888' }}>Hazards: </span>
                  {scanData.estimatedHazards.min}-{scanData.estimatedHazards.max}
                </div>
              </div>
              {scanData.hasBoss && (
                <div
                  style={{
                    marginTop: '8px',
                    color: '#ff4444',
                    fontWeight: 'bold',
                    fontSize: '12px',
                  }}
                >
                  [!] BOSS DETECTED
                </div>
              )}
            </div>

            {/* Rewards */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-around',
                marginBottom: '16px',
                padding: '8px',
                backgroundColor: '#1a1a0a',
                border: '1px solid #ffff0044',
              }}
            >
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '10px', color: '#666666' }}>LOOT</div>
                <div style={{ color: '#00ff88', fontWeight: 'bold' }}>
                  {scanData.lootMultiplier}x
                </div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '10px', color: '#666666' }}>XP</div>
                <div style={{ color: '#ffff00', fontWeight: 'bold' }}>
                  {scanData.xpMultiplier}x
                </div>
              </div>
            </div>

            {/* Enter Button */}
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={onClose}
                style={{
                  flex: 1,
                  backgroundColor: '#333333',
                  color: '#ffffff',
                  border: 'none',
                  padding: '12px',
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  cursor: 'pointer',
                }}
              >
                CLOSE
              </button>
              <button
                onClick={() => {
                  if (whirlpoolId && onEnterWhirlpool) {
                    onEnterWhirlpool(whirlpoolId);
                    onClose();
                  }
                }}
                disabled={!canEnter}
                style={{
                  flex: 2,
                  backgroundColor: canEnter ? CLASS_COLORS[scanData.class] : '#333333',
                  color: canEnter ? '#000000' : '#666666',
                  border: 'none',
                  padding: '12px',
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  cursor: canEnter ? 'pointer' : 'not-allowed',
                  fontWeight: 'bold',
                }}
              >
                {canEnter ? 'ENTER WHIRLPOOL' : `SAIL CLOSER (${distance}px)`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
